import Label from "@/components/forms/Label"
import Select from "@/components/forms/Select"
import { useState, useEffect } from "react"

/**
 * MixingModeTab Component
 *
 * Manages mixing mode settings with various stereo/mono options
 */
type MixingModeTabProps = {
  eqSettings: any
  mode: string
  onEqChange: (changes: any[]) => void
  defaultSettings: any
}

const MixingModeTab = ({ eqSettings, onEqChange, mode, defaultSettings }: MixingModeTabProps) => {
  const [mixingMode, setMixingMode] = useState<string>("STEREO")

  /**
   * On settings change
   *
   * When parent settings change, update mixing settings
   */
  useEffect(() => {
    if (eqSettings) {
      // Get default mixing settings based on current mode
      const defaultMixingSettings =
        defaultSettings.Configuration.DSP[mode]?.Mixing ||
        defaultSettings.Configuration.DSP.Headphone.Mixing

      // Handle missing Mixing settings in older presets
      const mixingSettings = eqSettings["Mixing"] || defaultMixingSettings

      setMixingMode(mixingSettings["Mode"]["Current"])
    }
  }, [eqSettings, mode])

  /**
   * Update mixing settings
   *
   * @param {*} domain
   * @param {*} value
   * @returns
   */
  const update = async (domain, value) => {
    try {
      await onEqChange([["Mixing", domain, value]])
      return true
    } catch (error) {
      // If the device update fails, revert to the current value
      setMixingMode(mixingMode)
      return false
    }
  }

  const handleMixingModeChange = async (keys) => {
    // NextUI Select passes a Set of selected keys
    const selectedKey = Array.from(keys)[0] as string
    setMixingMode(selectedKey)
    await update("Mode", selectedKey)
  }

  // Get settings with fallbacks for older presets
  const defaultMixingSettings =
    defaultSettings.Configuration.DSP[mode]?.Mixing ||
    defaultSettings.Configuration.DSP.Headphone.Mixing
  const mixingSettings = eqSettings?.["Mixing"] || defaultMixingSettings

  // Get available mixing mode options
  const mixingModeOptions = mixingSettings["Mode"]["Elements"].map((element) => ({
    value: element,
    label: element.replace(/_/g, " "), // Replace underscores with spaces for display
  }))

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Label
          text="Mid/Side Modes"
          info={
            <div className="max-w-md space-y-1">
              <p>
                These settings allow you to hear the mid channel (mono) or side channels of your
                audio mix:
              </p>
              <ul className="list-disc space-y-1 pl-4">
                <li>
                  <strong>STEREO:</strong> Normal stereo operation
                </li>
                <li>
                  <strong>MONO:</strong> Mix both channels together equally
                </li>
                <li>
                  <strong>LEFT TO BOTH:</strong> Send left channel to both outputs
                </li>
                <li>
                  <strong>RIGHT TO BOTH:</strong> Send right channel to both outputs
                </li>
                <li>
                  <strong>LEFT ONLY:</strong> Output left channel only, mute right
                </li>
                <li>
                  <strong>RIGHT ONLY:</strong> Output right channel only, mute left
                </li>
                <li>
                  <strong>LEFT MINUS RIGHT:</strong> Output the difference between channels
                </li>
              </ul>
              <p>
                Note: These settings are not saved in Cloud Presets, but will be saved with any
                Profile stored directly on your Element IV.
              </p>
            </div>
          }
        />
        <div className="flex max-w-xl items-center gap-x-4">
          <div className="flex-1">
            <Select
              selected={mixingMode}
              items={mixingModeOptions}
              onSelectionChange={handleMixingModeChange}
              className="w-full"
              listboxProps={{
                itemClasses: {
                  base: [
                    "py-1.5 rounded-md transition-none", // Added transition-none to disable animations
                    "data-[hover=true]:text-white",
                    "data-[hover=true]:bg-mine-shaft",
                    "data-[selectable=true]:focus:text-white",
                    "data-[selectable=true]:focus:bg-mine-shaft",
                  ],
                  title: "font-bulo text-sm font-light", // Reduced from text-base to text-sm
                },
              }}
              popoverProps={{
                radius: "sm",
                placement: "bottom-start",
                classNames: {
                  content:
                    "bg-dark rounded-lg border border-mine-shaft p-px max-h-64 overflow-y-auto",
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default MixingModeTab
